/**
 * **The mini forecast on every legal target** (`projects/target-mini-forecasts.md` 1.2, ruling
 * `decisions/0186` item 4): while an attack is armed, each unit the board rings carries a badge with
 * the net roll and the hit chance, `??` where this viewer's mask hides the defence.
 *
 * One forecast per target, through the same `attackIntentForAim` → `viewAttackForecast` →
 * `attackForecastRows` path the hover card (`TargetForecastPanel`) takes, so a badge and the card it
 * expands into cannot disagree. A target whose forecast is not an attack roll gets no badge and
 * keeps only its ring: an area weapon (`viewAttackForecast` is illegal for it), Scan, Lock On and
 * Bolster (`attackIntentForAim` returns `null`), and a Barrage or Full Tech with no card or option
 * armed.
 *
 * Barrage and Full Tech re-arm after each shot with the weapon or option cleared, so the badges go
 * away until the next card is picked and then follow it; the shot's own broadcast is a new `state`,
 * which recomputes them against the target's new condition.
 *
 * Forecasts are pure but not free, and the host re-renders on every hovered-token change, so the
 * whole map is memoized on the aim's content, the target set, the viewer, the overlay and the
 * `state` object (which changes only when the room broadcasts). A pointer move recomputes nothing.
 */
import { useMemo } from "react";
import type { ActorRole, EncounterState } from "@lancer-vtt/domain";
import { attackForecastRows, miniForecast, viewAttackForecast, type LoadedOverlay } from "@lancer-vtt/rules";
import type { ActionDraft } from "../../../lib/encounter/combatInspector";
import type { TargetBadgeSpec } from "../pixiCombatBoard";
import { attackIntentForAim } from "./attackIntentForAim";

export type TargetMiniForecastArgs = {
  state: EncounterState | null;
  /** The merged action overlay; `undefined` while it loads, which yields no badges. */
  overlay: LoadedOverlay | undefined;
  /** The ARMED aim (never a hover preview: badges belong to the one aim a click would fire). */
  aim: ActionDraft | null;
  /** The units the board rings as legal targets (`useTargetingWashes`' `legalTargetIds`). */
  targetIds: readonly string[] | null;
  viewer: { role: ActorRole; viewerSlot: string | null };
};

/** Every legal target's badge, keyed by target id. Targets with no attack-roll forecast are absent. */
export function targetMiniForecasts(args: TargetMiniForecastArgs): Map<string, TargetBadgeSpec> {
  const { state, overlay, aim, targetIds, viewer } = args;
  const out = new Map<string, TargetBadgeSpec>();
  if (!state || !overlay || !aim || !targetIds) return out;
  for (const targetId of targetIds) {
    const intent = attackIntentForAim(state, aim, targetId);
    if (!intent || intent.target.kind !== "unit") continue;
    const v = viewAttackForecast({
      state,
      overlay,
      viewer,
      attack: {
        attackerId: intent.attacker,
        targetId: intent.target.id,
        accuracy: intent.accuracy,
        ...(intent.weapon !== undefined ? { weapon: intent.weapon } : {}),
        ...(intent.profile !== undefined ? { profile: intent.profile } : {}),
        ...(intent.tech ? { tech: true } : {}),
        ...(intent.choices !== undefined ? { choices: intent.choices } : {}),
      },
    });
    if (!v.ok) continue;
    out.set(targetId, { ...miniForecast(attackForecastRows(state, v.forecast)), kind: v.forecast.techAttack ? "tech" : "attack" });
  }
  return out;
}

/** {@link targetMiniForecasts}, memoized per aim and state revision. `null` when nothing is armed. */
export function useTargetMiniForecasts(args: TargetMiniForecastArgs): ReadonlyMap<string, TargetBadgeSpec> | null {
  const { state, overlay, aim, targetIds, viewer } = args;
  // Keyed on content: the host rebuilds the aim and the id list objects on some renders.
  const aimKey = aim ? JSON.stringify(aim) : null;
  const idsKey = targetIds ? targetIds.join(",") : null;
  return useMemo(
    () => (aimKey === null ? null : targetMiniForecasts({ state, overlay, aim, targetIds, viewer })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the aim's and the ids' content, not their identity
    [state, overlay, aimKey, idsKey, viewer.role, viewer.viewerSlot],
  );
}
